import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Users2, User, ClipboardList, Filter, GripVertical } from "lucide-react";
import { AthleteSelect } from "@/components/AthleteSelect";
import { displayName, initials, sortAthletes, matchesSearch } from "@/lib/athleteName";
import { useAuth } from "@/context/AuthContext";
import LessonsArchive from "@/components/LessonsArchive";
import CompetitiveSessionsList from "@/components/CompetitiveSessionsList";
import LessonSkillEntries from "@/components/skills/LessonSkillEntries";
import BoutReportDialog from "@/components/skills/BoutReportDialog";
import SaveIssueBanner from "@/components/SaveIssueBanner";
import { newKey, isNetworkError, idemHeaders, checkSaved, saveDraft, loadDraft, clearDraft } from "@/lib/safeSave";
import { romeDate } from "@/lib/clubTime";

const SOURCE_LABEL = { lezione: "Lezione", lezione_rapida: "Lezione rapida", attivita_scherma: "Attività scherma" };
const localToday = () => romeDate();

function CoachSelect({ value, onChange, coaches, testid }) {
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Seleziona maestro" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">— Non indicato —</SelectItem>
        {coaches.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.email}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const BOUT_TYPES = ["libero", "tecnico", "tattico", "condizionato", "5 stoccate", "15 stoccate", "squadra", "simulazione gara"];
const BOUT_TYPE_TO_ACTIVITY = { libero: "libero", tecnico: "tecnico", tattico: "tattico", condizionato: "condizionato", "simulazione gara": "simulazione_gara" };
const ACTIVITIES = ["Gambe-scherma", "Tecnica", "Coppia", "Bersagli", "Lezione individuale", "Assalti", "Video analisi"];
const LESSON_TAGS = ["distanza", "tempo", "attacco", "difesa", "preparazione", "seconda intenzione", "gambe-scherma", "gestione tattica"];

const GROUP_BLOCK_CATEGORIES = [
  { v: "attivazione", l: "Attivazione" },
  { v: "prep_fisica", l: "Preparazione fisica" },
  { v: "gambe_scherma", l: "Gambe-scherma" },
  { v: "tecnica", l: "Tecnica" },
  { v: "tattica", l: "Tattica" },
  { v: "coppie", l: "Esercitazioni a coppie" },
  { v: "assalti_tema", l: "Assalti a tema" },
  { v: "assalti_liberi", l: "Assalti liberi" },
  { v: "circuito", l: "Circuito" },
  { v: "test", l: "Test CSP Lab" },
  { v: "simulazione", l: "Simulazione gara" },
  { v: "recupero", l: "Recupero e mobilità" },
  { v: "altro", l: "Altro" },
];

const ATT_STATUSES = [
  { v: "presente", l: "Presente", c: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { v: "parziale", l: "Parziale", c: "bg-blue-100 text-blue-800 border-blue-300" },
  { v: "assente", l: "Assente", c: "bg-red-100 text-red-800 border-red-300" },
  { v: "esonerato", l: "Esonerato", c: "bg-zinc-200 text-zinc-700 border-zinc-300" },
  { v: "infortunato", l: "Infortunato", c: "bg-amber-100 text-amber-800 border-amber-300" },
];

function catLabel(v) { return GROUP_BLOCK_CATEGORIES.find((c) => c.v === v)?.l || v || "—"; }
function statusLabel(v) { return ATT_STATUSES.find((s) => s.v === v) || { l: v, c: "bg-zinc-100 text-zinc-700" }; }

export default function Fencing() {
  const [athletes, setAthletes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [recent, setRecent] = useState({ rows: [], total: 0 });
  const [trainings, setTrainings] = useState([]);
  const [bouts, setBouts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [coaches, setCoaches] = useState([]);

  const [mode, setMode] = useState("individual"); // individual | group

  const { user } = useAuth();
  const defaultCoach = user && ["coach", "admin", "trainer"].includes(user.role) ? user.id : "";
  const [act, setAct] = useState({ athlete_id: "", coach_id: defaultCoach, date: new Date().toISOString().slice(0,10), activities: [], notes: "" });
  const [lesson, setLesson] = useState({ athlete_id: "", coach_id: defaultCoach, date: localToday(), duration: 20, objective: "", topics: "", corrections: "", tags: [], evaluation: 3, next_objective: "" });
  const [bout, setBout] = useState({ athlete_id: "", opponent: "", opponent_athlete_id: "", opponent_kind: "csp", date: new Date().toISOString().slice(0,10), score_for: 0, score_against: 0, bout_type: "libero", focus: "" });

  // Group form
  const initGroupForm = () => ({
    group_id: "", date: new Date().toISOString().slice(0,10), start_time: "17:00",
    coach_id: "", weapon: "", turn: "", duration: 90, objective: "", notes: "",
    blocks: [], attendance: [],
  });
  const [gForm, setGForm] = useState(initGroupForm());
  const [attOpen, setAttOpen] = useState(false);

  // History filters
  const [hMode, setHMode] = useState(""); // ""|individual|group
  const [hGroup, setHGroup] = useState("");
  const [hAthlete, setHAthlete] = useState("");
  const [hCoach, setHCoach] = useState("");
  const [hCategory, setHCategory] = useState("");
  const [hWeapon, setHWeapon] = useState("");
  const [hFrom, setHFrom] = useState("");
  const [hTo, setHTo] = useState("");

  const load = async () => {
    const safe = (p) => p.catch(() => ({ data: [] })); // un 403 (es. account atleta) non deve bloccare la pagina
    const [a, g, l, b, act2, u, tr] = await Promise.all([
      safe(api.get("/athletes")), safe(api.get("/groups")), safe(api.get("/lessons")), safe(api.get("/bouts")),
      safe(api.get("/fencing-activities")), safe(api.get("/coaches")), safe(api.get("/trainings")),
    ]);
    setAthletes(Array.from(new Map((a.data || []).filter((x) => !x.archived).map((x) => [x.id, x])).values())); setGroups(g.data); setLessons(l.data); setBouts(b.data); setActivities(act2.data);
    setCoaches(u.data || []); setTrainings(tr.data || []);
    api.get("/lessons-archive", { params: { all: 1, limit: 30, sort: "recent" } }).then((r) => setRecent({ rows: r.data.rows || [], total: r.data.total || 0 })).catch(() => {});
    const preTid = new URLSearchParams(window.location.search).get("training_id");
    if (preTid) { const t = (tr.data || []).find((x) => x.id === preTid); if (t) setLesson((s) => ({ ...s, training_id: t.id, date: (t.date || "").slice(0, 10) })); }
  };
  useEffect(() => { load(); }, []);

  const toggleAct = (a) => setAct((s) => ({ ...s, activities: s.activities.includes(a) ? s.activities.filter((x)=>x!==a) : [...s.activities, a] }));
  const toggleTag = (t) => setLesson((s) => ({ ...s, tags: s.tags.includes(t) ? s.tags.filter((x)=>x!==t) : [...s.tags, t] }));

  const saveAct = async () => {
    if (savingAct) return;
    if (!act.athlete_id) return toast.error("Seleziona l'atleta");
    setSavingAct(true);
    try {
      if (editingActId) { await api.put(`/fencing-activities/${editingActId}`, { ...act, activity_mode: "individual" }); toast.success("Attività aggiornata"); }
      else { await api.post("/fencing-activities", { ...act, activity_mode: "individual" }); toast.success("Attività registrata"); }
      setAct({ ...act, activities: [], notes: "" }); setEditingActId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSavingAct(false); }
  };
  const emptyLesson = () => ({ athlete_id: "", coach_id: defaultCoach, training_id: "", date: localToday(), duration: 20, objective: "", topics: "", corrections: "", tags: [], evaluation: 3, next_objective: "" });
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [savingLesson, setSavingLesson] = useState(false);
  const [lessonKey, setLessonKey] = useState(newKey);
  const [saveIssue, setSaveIssue] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [skills, setSkills] = useState([]);
  const [techExercises, setTechExercises] = useState([]);
  const [skillEntries, setSkillEntries] = useState([]);
  const [reportBout, setReportBout] = useState(null);
  useEffect(() => {
    api.get("/skills").then((r) => setSkills(r.data || [])).catch(() => {});
    api.get("/exercises").then((r) => setTechExercises((r.data || []).filter((x) => !x.archived))).catch(() => {});
  }, []);
  // Bozza della nuova lezione (sessione del browser, stesso utente): sopravvive a scadenza sessione e ricarica pagina
  useEffect(() => {
    if (!user?.id) return;
    const d = loadDraft("lesson");
    if (d && d.user_id === user.id && d.lesson) {
      setLesson(d.lesson); setSkillEntries(d.skillEntries || []); if (d.key) setLessonKey(d.key); setDraftRestored(true);
    }
  }, [user?.id]);
  useEffect(() => {
    if (!user?.id || editingLessonId) return;
    const filled = lesson.athlete_id || lesson.objective || lesson.topics || lesson.corrections || lesson.next_objective || skillEntries.length;
    if (filled) saveDraft("lesson", { user_id: user.id, lesson, skillEntries, key: lessonKey });
  }, [lesson, skillEntries, lessonKey, editingLessonId, user?.id]);
  const resetLessonForm = () => {
    setLesson(emptyLesson()); setEditingLessonId(null); setSkillEntries([]); setLessonKey(newKey()); setSaveIssue(null); setDraftRestored(false); clearDraft("lesson");
  };
  const saveSkills = async (lid) => {
    try {
      await api.put(`/lessons/${lid}/skill-entries`, { entries: skillEntries.map((e) => ({ id: e.id, skill_id: e.skill_id, exercise_id: e.exercise_id || null, context: e.context, rating: e.rating, text: e.text, addresses: e.addresses })) });
      return true;
    } catch (e2) {
      setEditingLessonId(lid); clearDraft("lesson"); toast.error(`Lezione salvata, ma competenze non salvate: ${e2.response?.data?.detail || "riprova"}`); load(); return false;
    }
  };
  const verifyLessonSave = async () => {
    try {
      const s = await checkSaved(saveIssue.key);
      if (s.status === "completed") {
        if (skillEntries.length && !(await saveSkills(s.resource_id))) { setSaveIssue(null); return; }
        toast.success("Verificato: la lezione risulta registrata"); resetLessonForm(); load();
      } else if (s.status === "pending") {
        setSaveIssue({ ...saveIssue, title: "Salvataggio ancora in elaborazione", message: "Attendi qualche secondo e verifica di nuovo." });
      } else {
        setSaveIssue({ ...saveIssue, title: "La lezione NON risulta registrata", message: "I dati sono ancora nel modulo: premi Riprova per salvarla." });
      }
    } catch (e) {
      setSaveIssue({ ...saveIssue, title: "Verifica non riuscita: connessione ancora assente", message: "I dati restano nel modulo. Riprova quando la rete torna disponibile." });
    }
  };
  const saveLesson = async () => {
    if (savingLesson) return;
    if (!lesson.athlete_id) return toast.error("Seleziona l'atleta");
    if (!editingLessonId) {
      const dup = lessons.find((l) => l.athlete_id === lesson.athlete_id && (l.date || "").slice(0, 10) === lesson.date && (l.coach_id || "") === (lesson.coach_id || ""));
      if (dup && !window.confirm("Esiste già una lezione per questo atleta in questa data con lo stesso maestro. Vuoi registrarne un'altra?")) return;
    }
    if (skillEntries.some((e) => !e.skill_id)) return toast.error("Seleziona la competenza in ogni riga (o rimuovi le righe vuote)");
    setSavingLesson(true);
    try {
      let lid = editingLessonId;
      if (editingLessonId) { await api.put(`/lessons/${editingLessonId}`, { ...lesson, training_id: lesson.training_id || null }); }
      else { const { data } = await api.post("/lessons", { ...lesson, training_id: lesson.training_id || null }, idemHeaders(lessonKey)); lid = data.id; }
      setSaveIssue(null);
      if (!(await saveSkills(lid))) return;
      toast.success(editingLessonId ? "Lezione aggiornata" : "Lezione registrata");
      resetLessonForm(); load();
    } catch (e) {
      if (isNetworkError(e) && !editingLessonId) { setSaveIssue({ key: lessonKey }); toast.error("Connessione interrotta: verifica il salvataggio"); }
      else if (isNetworkError(e)) { setSaveIssue({ key: null, title: "Connessione interrotta durante la modifica", message: "Le modifiche sono ancora nel modulo: premi Riprova." }); }
      else if (e.response?.status !== 401) toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
    finally { setSavingLesson(false); }
  };
  const editLesson = (l) => { setEditingLessonId(l.id); setLesson({ athlete_id: l.athlete_id, coach_id: l.coach_id || "", training_id: l.training_id || "", date: (l.date || "").slice(0, 10), duration: l.duration || 20, objective: l.objective || "", topics: l.topics || "", corrections: l.corrections || "", tags: l.tags || [], evaluation: l.evaluation || 3, next_objective: l.next_objective || "" }); setSkillEntries([]); api.get("/skill-observations", { params: { lesson_id: l.id } }).then((r) => setSkillEntries(r.data.map((o) => ({ ...o, key: o.id, exercise_id: o.exercise_id || "" })))); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const deleteLesson = async (l) => {
    if (!window.confirm("Eliminare questa lezione? L'operazione non è reversibile.")) return;
    try { await api.delete(`/lessons/${l.id}`); toast.success("Lezione eliminata"); if (editingLessonId === l.id) resetLessonForm(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };
  const emptyBout = () => ({ athlete_id: "", opponent: "", opponent_athlete_id: "", opponent_kind: "csp", date: new Date().toISOString().slice(0,10), score_for: 0, score_against: 0, bout_type: "libero", focus: "" });
  const [editingBoutId, setEditingBoutId] = useState(null);
  const startEditBout = (b) => {
    setEditingBoutId(b.id);
    setBout({ ...emptyBout(), ...b, opponent: b.opponent || "", opponent_athlete_id: b.opponent_athlete_id || "", opponent_kind: b.opponent_athlete_id ? "csp" : "external", score_for: b.score_for || 0, score_against: b.score_against || 0, bout_type: b.bout_type || "libero" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEditBout = () => { setEditingBoutId(null); setBout(emptyBout()); };
  const deleteBout = async (id) => {
    if (!window.confirm("Eliminare questo assalto? Sparirà dallo storico di entrambi gli atleti.")) return;
    try { await api.delete(`/bouts/${id}`); toast.success("Assalto eliminato"); if (editingBoutId === id) cancelEditBout(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };
  const saveBout = async () => {
    if (!bout.athlete_id) { toast.error("Seleziona l'atleta"); return; }
    const isCsp = bout.opponent_kind === "csp";
    if (isCsp && !bout.opponent_athlete_id) { toast.error("Seleziona l'avversario CSP"); return; }
    if (isCsp && bout.opponent_athlete_id === bout.athlete_id) { toast.error("Un atleta non può affrontare se stesso"); return; }
    if (!isCsp && !bout.opponent.trim()) { toast.error("Indica il nome dell'avversario esterno"); return; }
    if (bout.score_for === bout.score_against && !["V", "D"].includes(bout.result)) { toast.error("A parità di stoccate indica Vittoria o Sconfitta"); return; }
    const { opponent_kind, canonical_id, is_mirrored, ...rest } = bout;
    const b = {
      ...rest,
      result: bout.score_for === bout.score_against ? bout.result : null,
      opponent: isCsp ? null : bout.opponent.trim(),
      opponent_athlete_id: isCsp ? bout.opponent_athlete_id : null,
      context: bout.context || "training",
      format: bout.format || "single",
      activity_type: BOUT_TYPE_TO_ACTIVITY[bout.bout_type] || "altro",
    };
    try {
      if (editingBoutId) {
        await api.put(`/bouts/${editingBoutId}`, b);
        toast.success("Assalto aggiornato");
        cancelEditBout();
      } else {
        await api.post("/bouts", b);
        toast.success("Assalto registrato");
        setBout({ ...bout, opponent: "", opponent_athlete_id: "", score_for: 0, score_against: 0 });
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio assalto"); }
  };
  const athleteName = (id) => { const a = athletes.find((x) => x.id === id); return a ? displayName(a) : "Atleta CSP"; };
  const recentBouts = useMemo(() => [...bouts].sort((a, b) => (b.date || "").localeCompare(a.date || "") || (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 30), [bouts]);

  // Group logic
  const groupAthletes = useMemo(() => {
    if (!gForm.group_id) return [];
    return athletes.filter((a) => a.group_id === gForm.group_id && !a.archived);
  }, [athletes, gForm.group_id]);

  // Initialize attendance when group changes
  useEffect(() => {
    if (!gForm.group_id) return;
    setGForm((f) => ({
      ...f,
      attendance: groupAthletes.map((a) => {
        const existing = f.attendance.find((x) => x.athlete_id === a.id);
        return existing || { athlete_id: a.id, status: "presente", notes: "" };
      }),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gForm.group_id, athletes.length]);

  const addBlock = () => setGForm((f) => ({ ...f, blocks: [...f.blocks, { title: "", category: "attivazione", duration: 15, intensity: "media", description: "", exercises: "", equipment: "", notes: "" }] }));
  const updBlock = (i, patch) => setGForm((f) => { const b = [...f.blocks]; b[i] = { ...b[i], ...patch }; return { ...f, blocks: b }; });
  const removeBlock = (i) => setGForm((f) => ({ ...f, blocks: f.blocks.filter((_, idx) => idx !== i) }));

  const updAtt = (aid, patch) => setGForm((f) => ({ ...f, attendance: f.attendance.map((x) => x.athlete_id === aid ? { ...x, ...patch } : x) }));
  const setAllAttPresent = () => setGForm((f) => ({ ...f, attendance: f.attendance.map((x) => ({ ...x, status: "presente" })) }));

  const totalDuration = useMemo(() => gForm.blocks.reduce((s, b) => s + (parseInt(b.duration || 0) || 0), 0), [gForm.blocks]);
  const presentCount = gForm.attendance.filter((a) => a.status === "presente" || a.status === "parziale").length;

  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingActId, setEditingActId] = useState(null);
  const [savingAct, setSavingAct] = useState(false);
  const saveGroupActivity = async () => {
    if (savingAct) return;
    if (!gForm.group_id) return toast.error("Seleziona un gruppo");
    if (gForm.blocks.length === 0) return toast.error("Aggiungi almeno un blocco");
    setSavingAct(true);
    try {
      const payload = { ...gForm, duration: totalDuration || gForm.duration };
      if (editingGroupId) {
        const { data } = await api.put(`/fencing-activities/group/${editingGroupId}`, payload);
        toast.success(`Attività di gruppo aggiornata: ${data.present} presenti`);
      } else {
        const { data } = await api.post("/fencing-activities/group", payload);
        toast.success(`Attività di gruppo salvata: ${data.present} presenti (${data.individual_ids.length} storici individuali creati)`);
      }
      setGForm(initGroupForm()); setEditingGroupId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSavingAct(false); }
  };
  const editActivity = (a) => {
    if (a.activity_mode === "group") {
      setMode("group"); setEditingGroupId(a.id);
      setGForm({ ...initGroupForm(), group_id: a.group_id || "", date: (a.date || "").slice(0, 10), start_time: a.start_time || "", weapon: a.weapon || "", turn: a.turn || "", duration: a.duration || 60, objective: a.objective || "", notes: a.notes || "", coach_id: a.coach_id || "", blocks: a.blocks || [], attendance: a.attendance || [] });
    } else {
      setMode("individual"); setEditingActId(a.id);
      setAct({ athlete_id: a.athlete_id || "", coach_id: a.coach_id || "", date: (a.date || "").slice(0, 10), activities: a.activities || [], notes: a.notes || "" });
    }
    document.querySelector("[data-testid=tab-activity]")?.click();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const deleteActivity = async (a) => {
    const isGroup = a.activity_mode === "group";
    if (!window.confirm(isGroup ? "Eliminare l'attività di gruppo e tutti gli storici individuali collegati?" : "Eliminare questa attività individuale?")) return;
    try {
      await api.delete(isGroup ? `/fencing-activities/group/${a.id}` : `/fencing-activities/${a.id}`);
      toast.success("Attività eliminata"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };

  // History filtered
  const filteredActivities = useMemo(() => {
    return activities.filter((a) => {
      if (hMode && a.activity_mode !== hMode) return false;
      if (hGroup && a.group_id !== hGroup) return false;
      if (hAthlete && a.athlete_id !== hAthlete) return false;
      if (hCoach && a.coach_id !== hCoach) return false;
      if (hCategory && !(a.activities || []).includes(hCategory)) return false;
      if (hWeapon && a.weapon !== hWeapon) return false;
      if (hFrom && (a.date || "") < hFrom) return false;
      if (hTo && (a.date || "") > hTo) return false;
      return true;
    }).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [activities, hMode, hGroup, hAthlete, hCoach, hCategory, hWeapon, hFrom, hTo]);

  const clearFilters = () => { setHMode(""); setHGroup(""); setHAthlete(""); setHCoach(""); setHCategory(""); setHWeapon(""); setHFrom(""); setHTo(""); };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Attività Scherma</h1>
        <p className="text-zinc-500 mt-1">Registro sedute di gruppo, lezioni individuali, assalti.</p>
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "activity"}>
        <div className="overflow-x-auto -mx-1 px-1"><TabsList className="w-max">
          <TabsTrigger value="activity" data-testid="tab-activity">Attività</TabsTrigger>
          <TabsTrigger value="bout-quick" data-testid="tab-bout-quick">Assalto Rapido</TabsTrigger>
          <TabsTrigger value="lesson" data-testid="tab-lesson">Lezione</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history-fencing">Storico</TabsTrigger>
          <TabsTrigger value="lessons-archive" data-testid="tab-lessons-archive">Archivio lezioni</TabsTrigger>
          <TabsTrigger value="simulation" data-testid="tab-simulation">Simulazione Gara</TabsTrigger>
        </TabsList></div>

        <TabsContent value="simulation" className="pt-6"><CompetitiveSessionsList /></TabsContent>
        <TabsContent value="lessons-archive" className="pt-6">
          <LessonsArchive athletes={athletes} groups={groups} coaches={coaches} />
        </TabsContent>

        <TabsContent value="activity" className="pt-6 space-y-4">
          <div className="inline-flex bg-white border border-zinc-200 rounded-md p-1">
            <button data-testid="mode-individual" onClick={() => setMode("individual")} className={`px-4 py-2 text-sm rounded flex items-center gap-2 ${mode === "individual" ? "bg-black text-white" : "text-zinc-600"}`}>
              <User className="w-4 h-4" />Singolo atleta
            </button>
            <button data-testid="mode-group" onClick={() => setMode("group")} className={`px-4 py-2 text-sm rounded flex items-center gap-2 ${mode === "group" ? "bg-black text-white" : "text-zinc-600"}`}>
              <Users2 className="w-4 h-4" />Gruppo
            </button>
          </div>

          {mode === "individual" && (
            <div className="max-w-2xl space-y-4">
              <div>
                <Label>Atleta</Label>
                <AthleteSelect
                  value={act.athlete_id}
                  onChange={(v) => setAct({ ...act, athlete_id: v })}
                  athletes={athletes}
                  groups={groups}
                  testid="ind-athlete"
                  placeholder="Seleziona atleta"
                />
              </div>
              <div><Label>Data</Label><Input type="date" value={act.date} onChange={(e) => setAct({...act, date: e.target.value})} /></div>
              <div><Label>Maestro</Label><CoachSelect value={act.coach_id} onChange={(v) => setAct({ ...act, coach_id: v })} coaches={coaches} testid="ind-coach" /></div>
              <div>
                <Label>Attività svolte</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {ACTIVITIES.map((a) => (
                    <button key={a} onClick={() => toggleAct(a)} data-testid={`activity-${a}`} className={`tap-target-lg px-3 py-2 text-left text-sm rounded-md border ${act.activities.includes(a) ? "bg-black text-white border-black" : "bg-white border-zinc-200"}`}>{a}</button>
                  ))}
                </div>
              </div>
              <div><Label>Note</Label><Textarea rows={3} value={act.notes} onChange={(e) => setAct({...act, notes: e.target.value})} /></div>
              <div className="flex gap-2">
                <Button data-testid="activity-save" onClick={saveAct} disabled={savingAct} className="flex-1 h-12 bg-black hover:bg-zinc-800">{savingAct ? "Salvo…" : editingActId ? "Aggiorna attività individuale" : "Salva attività individuale"}</Button>
                {editingActId && <Button data-testid="activity-cancel-edit" variant="outline" className="h-12" onClick={() => { setEditingActId(null); setAct({ ...act, activities: [], notes: "" }); }}>Annulla modifica</Button>}
              </div>
              {editingActId && <div className="text-xs text-amber-700" data-testid="activity-editing-banner">Stai modificando un'attività esistente.</div>}
            </div>
          )}

          {mode === "group" && (
            <div className="space-y-4">
              <div className="bg-white border border-zinc-200 rounded-md p-5 space-y-4">
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <Label>Gruppo *</Label>
                    <Select value={gForm.group_id} onValueChange={(v) => setGForm({...gForm, group_id: v})}>
                      <SelectTrigger data-testid="grp-select"><SelectValue placeholder="Seleziona gruppo" /></SelectTrigger>
                      <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data *</Label>
                    <Input data-testid="grp-date" type="date" value={gForm.date} onChange={(e) => setGForm({...gForm, date: e.target.value})} />
                  </div>
                  <div>
                    <Label>Orario</Label>
                    <Input data-testid="grp-time" type="time" value={gForm.start_time} onChange={(e) => setGForm({...gForm, start_time: e.target.value})} />
                  </div>
                  <div>
                    <Label>Tecnico responsabile</Label>
                    <Select value={gForm.coach_id || "auto"} onValueChange={(v) => setGForm({...gForm, coach_id: v === "auto" ? "" : v})}>
                      <SelectTrigger><SelectValue placeholder="Automatico" /></SelectTrigger>
                      <SelectContent><SelectItem value="auto">Automatico (me)</SelectItem>{coaches.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Arma</Label>
                    <Select value={gForm.weapon || "any"} onValueChange={(v) => setGForm({...gForm, weapon: v === "any" ? "" : v})}>
                      <SelectTrigger><SelectValue placeholder="Del gruppo" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Del gruppo</SelectItem>
                        <SelectItem value="fioretto">Fioretto</SelectItem>
                        <SelectItem value="spada">Spada</SelectItem>
                        <SelectItem value="sciabola">Sciabola</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Turno</Label>
                    <Input data-testid="grp-turn" value={gForm.turn} onChange={(e) => setGForm({...gForm, turn: e.target.value})} placeholder="es. II turno" />
                  </div>
                </div>
                <div>
                  <Label>Obiettivo della seduta</Label>
                  <Input data-testid="grp-objective" value={gForm.objective} onChange={(e) => setGForm({...gForm, objective: e.target.value})} placeholder="es. accelerazione gambe-scherma e gestione della distanza" />
                </div>
                <div>
                  <Label>Note generali</Label>
                  <Textarea rows={2} data-testid="grp-notes" value={gForm.notes} onChange={(e) => setGForm({...gForm, notes: e.target.value})} />
                </div>

                <div className="flex items-center justify-between pt-2 text-sm">
                  <div className="flex items-center gap-4">
                    <span className="text-zinc-500">Durata totale: <strong className="font-mono text-black">{totalDuration || 0}′</strong></span>
                    <span className="text-zinc-500">Blocchi: <strong className="font-mono text-black">{gForm.blocks.length}</strong></span>
                    <span className="text-zinc-500">Atleti nel gruppo: <strong className="font-mono text-black">{groupAthletes.length}</strong> · Presenti: <strong className="font-mono text-emerald-600">{presentCount}</strong></span>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setAttOpen(true)} data-testid="grp-attendance-btn" disabled={!gForm.group_id}>
                    <ClipboardList className="w-3.5 h-3.5 mr-1" />Presenze
                  </Button>
                </div>
              </div>

              {/* Blocks */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold">Blocchi della seduta</h3>
                  <Button data-testid="grp-add-block" onClick={addBlock} size="sm" className="bg-black hover:bg-zinc-800"><Plus className="w-3.5 h-3.5 mr-1" />Blocco</Button>
                </div>
                {gForm.blocks.length === 0 && (
                  <div className="bg-white border border-dashed border-zinc-300 rounded-md p-8 text-center text-sm text-zinc-500">
                    Nessun blocco. Aggiungi il primo (es. Attivazione, Preparazione fisica, Tecnica…).
                  </div>
                )}
                {gForm.blocks.map((b, i) => (
                  <div key={i} data-testid={`grp-block-${i}`} className="bg-white border border-zinc-200 rounded-md p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-zinc-300" />
                      <span className="text-xs font-mono text-zinc-500">Blocco {i + 1}</span>
                      <button onClick={() => removeBlock(i)} className="ml-auto text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid md:grid-cols-3 gap-2">
                      <div className="md:col-span-2"><Label className="text-xs">Titolo</Label><Input value={b.title} onChange={(e) => updBlock(i, { title: e.target.value })} placeholder="es. Passo avanti, indietro e affondo" /></div>
                      <div><Label className="text-xs">Categoria</Label>
                        <Select value={b.category} onValueChange={(v) => updBlock(i, { category: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{GROUP_BLOCK_CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Durata (min)</Label><Input type="number" value={b.duration} onChange={(e) => updBlock(i, { duration: parseInt(e.target.value || 0) })} /></div>
                      <div><Label className="text-xs">Intensità</Label>
                        <Select value={b.intensity} onValueChange={(v) => updBlock(i, { intensity: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bassa">Bassa</SelectItem>
                            <SelectItem value="media">Media</SelectItem>
                            <SelectItem value="alta">Alta</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Obiettivo del blocco</Label><Input value={b.objective || ""} onChange={(e) => updBlock(i, { objective: e.target.value })} /></div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-2">
                      <div><Label className="text-xs">Esercizi</Label><Textarea rows={2} value={b.exercises || ""} onChange={(e) => updBlock(i, { exercises: e.target.value })} placeholder="Descrizione degli esercizi eseguiti" /></div>
                      <div><Label className="text-xs">Materiale</Label><Textarea rows={2} value={b.equipment || ""} onChange={(e) => updBlock(i, { equipment: e.target.value })} placeholder="Coni, elastici, palline…" /></div>
                    </div>
                    <div><Label className="text-xs">Osservazioni del tecnico</Label><Textarea rows={2} value={b.notes || ""} onChange={(e) => updBlock(i, { notes: e.target.value })} /></div>
                  </div>
                ))}
              </div>

              <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-zinc-200 py-3 -mx-4 lg:-mx-8 px-4 lg:px-8 flex gap-2">
                <Button data-testid="grp-save" onClick={saveGroupActivity} disabled={savingAct} className="flex-1 h-12 bg-black hover:bg-zinc-800">
                  {savingAct ? "Salvo…" : `${editingGroupId ? "Aggiorna" : "Salva"} attività di gruppo · ${gForm.blocks.length} blocchi · ${presentCount} presenti`}
                </Button>
                {editingGroupId && <Button data-testid="grp-cancel-edit" variant="outline" className="h-12" onClick={() => { setEditingGroupId(null); setGForm(initGroupForm()); }}>Annulla modifica</Button>}
              </div>

              <Dialog open={attOpen} onOpenChange={setAttOpen}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Presenze — {groups.find((g) => g.id === gForm.group_id)?.name}</DialogTitle>
                    <DialogDescription>Solo atleti presenti o parziali riceveranno la registrazione nel proprio storico.</DialogDescription>
                  </DialogHeader>
                  <div className="flex justify-between text-sm py-2 border-b border-zinc-100">
                    <div><strong>{presentCount}</strong> / {groupAthletes.length} presenti</div>
                    <Button size="sm" variant="outline" onClick={setAllAttPresent} data-testid="att-all-present">Tutti presenti</Button>
                  </div>
                  <div className="space-y-1 max-h-96 overflow-y-auto -mx-6 px-6">
                    {sortAthletes(groupAthletes).map((a) => {
                      const att = gForm.attendance.find((x) => x.athlete_id === a.id) || { status: "presente" };
                      const sl = statusLabel(att.status);
                      return (
                        <div key={a.id} data-testid={`att-row-${a.id}`} className="flex items-center gap-3 py-2 border-b border-zinc-50">
                          <div className="flex-1">
                            <div className="text-sm font-medium">{displayName(a)}</div>
                            <div className="text-xs text-zinc-500">{a.weapon}</div>
                          </div>
                          <Select value={att.status} onValueChange={(v) => updAtt(a.id, { status: v })}>
                            <SelectTrigger className={`h-8 w-36 text-xs ${sl.c}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{ATT_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                    {groupAthletes.length === 0 && <div className="text-center py-6 text-zinc-500 text-sm">Nessun atleta assegnato a questo gruppo.</div>}
                  </div>
                  <DialogFooter>
                    <Button onClick={() => setAttOpen(false)} className="bg-black hover:bg-zinc-800">Conferma</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </TabsContent>

        <TabsContent value="bout-quick" className="pt-6">
          <div className="max-w-2xl space-y-4">
            <div>
              <Label>Atleta</Label>
              <AthleteSelect
                value={bout.athlete_id}
                onChange={(v) => setBout({ ...bout, athlete_id: v, opponent_athlete_id: bout.opponent_athlete_id === v ? "" : bout.opponent_athlete_id })}
                athletes={athletes}
                groups={groups}
                excludeIds={bout.opponent_kind === "csp" && bout.opponent_athlete_id ? [bout.opponent_athlete_id] : []}
                placeholder="Seleziona atleta"
                testid="bout-athlete"
                triggerClassName="h-14 text-lg"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Avversario</Label>
                <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden text-xs" data-testid="bout-opponent-kind">
                  <button type="button" data-testid="bout-opponent-kind-csp" onClick={() => setBout({ ...bout, opponent_kind: "csp", opponent: "" })} className={`px-3 py-1.5 ${bout.opponent_kind === "csp" ? "bg-black text-white" : "bg-white text-zinc-600"}`}>Atleta CSP</button>
                  <button type="button" data-testid="bout-opponent-kind-external" onClick={() => setBout({ ...bout, opponent_kind: "external", opponent_athlete_id: "" })} className={`px-3 py-1.5 ${bout.opponent_kind === "external" ? "bg-black text-white" : "bg-white text-zinc-600"}`}>Esterno</button>
                </div>
              </div>
              {bout.opponent_kind === "csp" ? (
                <AthleteSelect
                  value={bout.opponent_athlete_id}
                  onChange={(v) => setBout({ ...bout, opponent_athlete_id: v })}
                  athletes={athletes}
                  groups={groups}
                  excludeIds={bout.athlete_id ? [bout.athlete_id] : []}
                  placeholder="Seleziona avversario CSP"
                  testid="bout-opponent-athlete"
                  triggerClassName="h-14 text-lg"
                />
              ) : (
                <Input data-testid="bout-opponent" className="h-14 text-lg" placeholder="Nome avversario esterno" value={bout.opponent} onChange={(e) => setBout({...bout, opponent: e.target.value})} />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Punti a favore</Label>
                <div className="flex items-center gap-2">
                  <button data-testid="bout-for-minus" onClick={() => setBout({...bout, score_for: Math.max(0, bout.score_for-1)})} className="tap-target-xl w-16 bg-zinc-100 rounded-md text-2xl font-bold">-</button>
                  <div className="flex-1 h-16 flex items-center justify-center font-mono text-4xl bg-white border border-zinc-200 rounded-md">{bout.score_for}</div>
                  <button data-testid="bout-for-plus" onClick={() => setBout({...bout, score_for: bout.score_for+1})} className="tap-target-xl w-16 bg-black text-white rounded-md text-2xl font-bold">+</button>
                </div>
              </div>
              <div>
                <Label>Punti subiti</Label>
                <div className="flex items-center gap-2">
                  <button data-testid="bout-against-minus" onClick={() => setBout({...bout, score_against: Math.max(0, bout.score_against-1)})} className="tap-target-xl w-16 bg-zinc-100 rounded-md text-2xl font-bold">-</button>
                  <div className="flex-1 h-16 flex items-center justify-center font-mono text-4xl bg-white border border-zinc-200 rounded-md">{bout.score_against}</div>
                  <button data-testid="bout-against-plus" onClick={() => setBout({...bout, score_against: bout.score_against+1})} className="tap-target-xl w-16 bg-black text-white rounded-md text-2xl font-bold">+</button>
                </div>
              </div>
            </div>
            {bout.score_for === bout.score_against && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="bout-tie">
                <div className="text-xs text-amber-800">Parità di stoccate: indica l'esito esplicito (es. V{bout.score_for} / D{bout.score_for}).</div>
                <div className="flex gap-2">
                  <button data-testid="bout-result-v" onClick={() => setBout({ ...bout, result: "V" })} className={`flex-1 text-sm py-2 rounded border ${bout.result === "V" ? "bg-black text-white border-black" : "border-zinc-300 bg-white"}`}>Vittoria</button>
                  <button data-testid="bout-result-d" onClick={() => setBout({ ...bout, result: "D" })} className={`flex-1 text-sm py-2 rounded border ${bout.result === "D" ? "bg-black text-white border-black" : "border-zinc-300 bg-white"}`}>Sconfitta</button>
                </div>
              </div>
            )}
            <div><Label>Data</Label><Input type="date" data-testid="bout-date" className="h-11" value={bout.date} onChange={(e) => setBout({ ...bout, date: e.target.value })} /></div>
            <div>
              <Label>Tipo</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {BOUT_TYPES.map((t) => (
                  <button key={t} onClick={() => setBout({...bout, bout_type: t})} data-testid={`bout-type-${t}`} className={`tap-target text-xs px-2 py-2 rounded-md border ${bout.bout_type === t ? "bg-black text-white border-black" : "bg-white border-zinc-200"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="bout-save" onClick={saveBout} className="flex-1 h-14 text-lg bg-black hover:bg-zinc-800">{editingBoutId ? "Aggiorna assalto" : "Registra assalto"}</Button>
              {editingBoutId && <Button data-testid="bout-cancel-edit" variant="outline" className="h-14" onClick={cancelEditBout}>Annulla</Button>}
            </div>
            {editingBoutId && <div className="text-xs text-amber-700" data-testid="bout-editing-banner">Stai modificando un assalto esistente: le statistiche di entrambi gli atleti si aggiorneranno.</div>}
          </div>

          <div className="mt-8 max-w-2xl space-y-2" data-testid="bouts-recent">
            {reportBout && <BoutReportDialog bout={reportBout} athletes={athletes} skills={skills} onClose={() => setReportBout(null)} />}
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Assalti recenti · {bouts.length}</div>
            {recentBouts.length === 0 && <div className="text-sm text-zinc-500">Nessun assalto registrato.</div>}
            {recentBouts.map((b) => (
              <div key={b.id} data-testid={`bout-item-${b.id}`} className={`flex items-center gap-3 border rounded-md px-3 py-2 text-sm ${editingBoutId === b.id ? "border-amber-400 bg-amber-50" : "border-zinc-200 bg-white"}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${b.result === "V" ? "bg-emerald-100 text-emerald-800" : b.result === "D" ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-600"}`}>{b.result || "="}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{athleteName(b.athlete_id)} <span className="font-mono">{b.score_for}–{b.score_against}</span> {b.opponent_athlete_id ? athleteName(b.opponent_athlete_id) : <>{b.opponent || "—"} <span className="text-[10px] text-zinc-400 uppercase">esterno</span></>}</div>
                  <div className="text-xs text-zinc-500 font-mono">{b.date} · {b.bout_type}</div>
                </div>
                <button data-testid={`bout-report-${b.id}`} onClick={() => setReportBout(b)} className="tap-target text-xs px-2 py-1 rounded border border-zinc-300 hover:bg-zinc-50">Report</button>
                <button data-testid={`bout-edit-${b.id}`} onClick={() => startEditBout(b)} className="tap-target text-xs px-2 py-1 rounded border border-zinc-300 hover:bg-zinc-50">Modifica</button>
                <button data-testid={`bout-delete-${b.id}`} onClick={() => deleteBout(b.id)} className="tap-target p-1.5 rounded text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="lesson" className="pt-6">
          {draftRestored && !editingLessonId && (
            <div className="mb-4 border border-zinc-300 bg-zinc-50 rounded-md px-3 py-2 text-sm text-zinc-700 flex items-center gap-2" data-testid="lesson-draft-restored">
              Bozza non salvata ripristinata. <button className="underline" data-testid="lesson-draft-discard" onClick={resetLessonForm}>Scarta bozza</button>
            </div>
          )}
          <div className="max-w-2xl space-y-4">
            <div>
              <Label>Atleta</Label>
              <Select value={lesson.athlete_id} onValueChange={(v) => setLesson({...lesson, athlete_id: v})}>
                <SelectTrigger data-testid="lesson-athlete"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{sortAthletes(athletes).map((a) => <SelectItem key={a.id} value={a.id}>{displayName(a)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Data</Label><Input type="date" data-testid="lesson-date" value={lesson.date} onChange={(e) => setLesson({...lesson, date: e.target.value, training_id: ""})} /></div>
              <div><Label>Maestro</Label><CoachSelect value={lesson.coach_id} onChange={(v) => setLesson({ ...lesson, coach_id: v })} coaches={coaches} testid="lesson-coach" /></div>
              <div><Label>Durata (min)</Label><Input type="number" value={lesson.duration} onChange={(e) => setLesson({...lesson, duration: parseInt(e.target.value || 0)})} /></div>
            </div>
            <div>
              <Label>Seduta <span className="text-zinc-400 font-normal">(facoltativa · compare nel diario solo di questa seduta)</span></Label>
              <Select value={lesson.training_id || "none"} onValueChange={(v) => setLesson({ ...lesson, training_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="lesson-training"><SelectValue placeholder="Nessuna seduta" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna seduta (lezione non collegata)</SelectItem>
                  {trainings.filter((t) => (t.date || "").slice(0, 10) === lesson.date).map((t) => <SelectItem key={t.id} value={t.id} data-testid={`lesson-training-opt-${t.id}`}>{t.title}{t.date?.length > 10 ? ` · ${t.date.slice(11, 16)}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Obiettivo <span className="text-zinc-400 font-normal">(facoltativo)</span></Label><Input data-testid="lesson-objective" value={lesson.objective} onChange={(e) => setLesson({...lesson, objective: e.target.value})} /></div>
            <div><Label>Argomenti</Label><Textarea rows={2} value={lesson.topics} onChange={(e) => setLesson({...lesson, topics: e.target.value})} /></div>
            <div><Label>Correzioni</Label><Textarea rows={2} value={lesson.corrections} onChange={(e) => setLesson({...lesson, corrections: e.target.value})} /></div>
            <div><Label>Competenze lavorate</Label><div className="mt-2"><LessonSkillEntries athleteId={lesson.athlete_id} entries={skillEntries} setEntries={setSkillEntries} skills={skills} exercises={techExercises} /></div></div>
            <div>
              <Label>Tag tecnici</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {LESSON_TAGS.map((t) => (
                  <button key={t} onClick={() => toggleTag(t)} className={`px-3 py-2 text-sm rounded-md border ${lesson.tags.includes(t) ? "bg-black text-white border-black" : "bg-white border-zinc-200"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div><Label>Valutazione (1-5)</Label><Input type="number" min={1} max={5} value={lesson.evaluation} onChange={(e) => setLesson({...lesson, evaluation: parseInt(e.target.value || 3)})} /></div>
            <div><Label>Prossimo obiettivo</Label><Input value={lesson.next_objective} onChange={(e) => setLesson({...lesson, next_objective: e.target.value})} /></div>
            <div className="flex gap-2">
              <Button data-testid="lesson-save" onClick={saveLesson} disabled={savingLesson} className="flex-1 h-12 bg-black hover:bg-zinc-800">{savingLesson ? "Salvo…" : editingLessonId ? "Aggiorna lezione" : "Salva lezione"}</Button>
              {editingLessonId && <Button data-testid="lesson-cancel-edit" variant="outline" className="h-12" onClick={resetLessonForm}>Annulla modifica</Button>}
            </div>
            {editingLessonId && <div className="text-xs text-amber-700" data-testid="lesson-editing-banner">Stai modificando una lezione esistente.</div>}
            <SaveIssueBanner issue={saveIssue} onVerify={saveIssue?.key ? verifyLessonSave : saveLesson} onRetry={saveLesson} testid="lesson-save-issue" />
          </div>

          <div className="mt-8 space-y-2" data-testid="lessons-recent">
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono" data-testid="lessons-recent-count">Ultime registrate · {Math.min(30, recent.total)} di {recent.total} (tutte le fonti)</div>
            {recent.total === 0 && <div className="text-sm text-zinc-500">Nessuna lezione registrata.</div>}
            {recent.rows.map((r) => {
              const isForm = r.source === "lezione";
              return (
                <div key={`${r.source}-${r.id}-${r.athlete_id}`} data-testid={`lesson-row-${r.id}`} className={`bg-white border rounded-md px-4 py-3 flex items-center gap-4 ${editingLessonId === r.id ? "border-black" : "border-zinc-200"}`}>
                  <div className="font-mono text-xs text-zinc-500 w-24 shrink-0">{r.date || "senza data"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.athlete_name} <span className="text-zinc-400 font-normal">· {r.duration || 0}′ · {r.coach_name}</span></div>
                    <div className="text-xs text-zinc-500 truncate">{r.objective || "—"}{r.evaluation ? ` · val. ${r.evaluation}/5` : ""}{r.warning ? ` · ${r.warning}` : ""}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 shrink-0" data-testid={`lesson-source-${r.id}`}>{SOURCE_LABEL[r.source]}</span>
                  {isForm && <button data-testid={`lesson-edit-${r.id}`} onClick={() => api.get(`/lessons/${r.id}`).then((x) => editLesson(x.data)).catch(() => toast.error("Lezione non trovata"))} className="text-xs uppercase tracking-wider text-[#0052FF] hover:underline shrink-0">Modifica</button>}
                  {isForm && <button data-testid={`lesson-delete-${r.id}`} onClick={() => deleteLesson(r)} className="text-zinc-400 hover:text-red-600 shrink-0" title="Elimina"><Trash2 className="w-4 h-4" /></button>}
                </div>
              );
            })}
            {recent.total > 30 && <a href="/scherma?tab=lessons-archive" className="text-xs uppercase tracking-wider text-[#0052FF] hover:underline" data-testid="lessons-recent-all">Vedi tutte nell'archivio →</a>}
          </div>
        </TabsContent>

        <TabsContent value="history" className="pt-6 space-y-4">
          <div className="bg-white border border-zinc-200 rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Filter className="w-4 h-4 text-zinc-400" /><span className="font-heading font-semibold text-sm">Filtri</span></div>
              <Button size="sm" variant="outline" onClick={clearFilters} data-testid="fen-clear">Reset</Button>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
              <div><Label className="text-xs">Modalità</Label>
                <Select value={hMode || "all"} onValueChange={(v) => setHMode(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="f-mode" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutte</SelectItem><SelectItem value="individual">Individuali</SelectItem><SelectItem value="group">Di gruppo</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Gruppo</Label>
                <Select value={hGroup || "all"} onValueChange={(v) => setHGroup(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Tutti" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutti</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Atleta</Label>
                <Select value={hAthlete || "all"} onValueChange={(v) => setHAthlete(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Tutti" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutti</SelectItem>{sortAthletes(athletes).map((a) => <SelectItem key={a.id} value={a.id}>{displayName(a)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Tecnico</Label>
                <Select value={hCoach || "all"} onValueChange={(v) => setHCoach(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Tutti" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutti</SelectItem>{coaches.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Categoria attività</Label>
                <Select value={hCategory || "all"} onValueChange={(v) => setHCategory(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Tutte" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutte</SelectItem>{GROUP_BLOCK_CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Arma</Label>
                <Select value={hWeapon || "all"} onValueChange={(v) => setHWeapon(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Tutte" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutte</SelectItem><SelectItem value="fioretto">Fioretto</SelectItem><SelectItem value="spada">Spada</SelectItem><SelectItem value="sciabola">Sciabola</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Dal</Label><Input type="date" value={hFrom} onChange={(e) => setHFrom(e.target.value)} className="h-9" /></div>
              <div><Label className="text-xs">Al</Label><Input type="date" value={hTo} onChange={(e) => setHTo(e.target.value)} className="h-9" /></div>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="text-left px-5 py-3 w-24">Modalità</th>
                  <th className="text-left px-5 py-3 w-24">Data</th>
                  <th className="text-left px-5 py-3">Gruppo / Atleta</th>
                  <th className="text-left px-5 py-3">Attività</th>
                  <th className="text-left px-5 py-3 w-24">Durata</th>
                  <th className="text-left px-5 py-3">Note</th>
                  <th className="px-3 py-3 w-28" />
                </tr>
              </thead>
              <tbody>
                {filteredActivities.slice(0, 200).map((act) => {
                  const isGroup = act.activity_mode === "group";
                  const isIndivLinked = act.activity_mode === "individual" && act.parent_group_activity_id;
                  const g = groups.find((x) => x.id === act.group_id);
                  const a = athletes.find((x) => x.id === act.athlete_id);
                  return (
                    <tr key={act.id} data-testid={`fen-row-${act.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-5 py-3">
                        {isGroup && <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-blue-100 text-blue-800 border border-blue-300 flex items-center gap-1 w-fit"><Users2 className="w-3 h-3" />Gruppo</span>}
                        {!isGroup && isIndivLinked && <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-sky-50 text-sky-700 border border-sky-200 flex items-center gap-1 w-fit"><Users2 className="w-3 h-3" />da gruppo</span>}
                        {!isGroup && !isIndivLinked && <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-zinc-100 text-zinc-700 border border-zinc-200 flex items-center gap-1 w-fit"><User className="w-3 h-3" />Individuale</span>}
                      </td>
                      <td className="px-5 py-3 font-mono text-sm">{act.date}{act.start_time ? ` ${act.start_time}` : ""}</td>
                      <td className="px-5 py-3">
                        {isGroup ? (g?.name || "—") : displayName(a)}
                        {act.coach_id && <div className="text-[10px] text-zinc-500" data-testid={`fen-coach-${act.id}`}>Maestro: {coaches.find((c) => c.id === act.coach_id)?.name || "—"}</div>}
                        {isGroup && <div className="text-[10px] text-zinc-500 font-mono">{act.present_count} presenti · {act.absent_count} assenti</div>}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(act.activities || []).slice(0, 4).map((cat) => (
                            <span key={cat} className="text-[10px] px-2 py-0.5 bg-zinc-100 rounded">{catLabel(cat)}</span>
                          ))}
                          {(act.activities || []).length > 4 && <span className="text-[10px] px-2 py-0.5 bg-zinc-100 rounded">+{act.activities.length - 4}</span>}
                        </div>
                      </td>
                      <td className="px-5 py-3 font-mono text-sm">{act.duration ? `${act.duration}′` : "—"}</td>
                      <td className="px-5 py-3 text-xs text-zinc-600">{act.objective || act.notes || "—"}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {isIndivLinked ? <span className="text-[10px] text-zinc-400" title="Modifica dall'attività di gruppo">da gruppo</span> : (
                          <div className="flex items-center gap-3">
                            <button data-testid={`fen-edit-${act.id}`} onClick={() => editActivity(act)} className="text-xs uppercase tracking-wider text-[#0052FF] hover:underline">Modifica</button>
                            <button data-testid={`fen-delete-${act.id}`} onClick={() => deleteActivity(act)} className="text-zinc-400 hover:text-red-600" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredActivities.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-zinc-500">Nessuna attività registrata.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
